/**
 * Proposal copy: the propose-a-task form, its prerequisite picker, and the
 * Markdown editor's controls.
 */

export interface TaskProposalCopy {
  readonly propose: {
    readonly title: string;
    readonly titleLabel: string;
    readonly titleHint: string;
    readonly typeLabel: string;
    readonly typeDefinitions: Readonly<Record<string, string>>;
    readonly difficultyLabel: string;
    readonly difficultyDefinitions: Readonly<Record<string, string>>;
    readonly suggestedPriorityLabel: string;
    readonly suggestedPriorityHint: string;
    readonly descriptionLabel: string;
    readonly descriptionHint: string;
    readonly prerequisitesLabel: string;
    readonly prerequisitesHint: string;
    readonly prerequisitesSearchLabel: string;
    readonly prerequisitesSearchPlaceholder: string;
    readonly prerequisitesLoading: string;
    readonly prerequisitesFailed: string;
    readonly prerequisitesEmpty: string;
    readonly prerequisitesNoMatches: string;
    readonly selectedPrerequisites: (count: number) => string;
    readonly removePrerequisite: (reference: string) => string;
    readonly submit: string;
    readonly submitting: string;
    readonly successTitle: string;
    readonly successBody: string;
    readonly viewTask: string;
    readonly errorSummary: string;
    readonly titleMissing: string;
    readonly descriptionMissing: string;
  };

  readonly markdown: {
    readonly edit: string;
    readonly preview: string;
    readonly previewEmpty: string;
    readonly previewLabel: string;
  };
}

export const TASK_PROPOSAL_COPY_EN: TaskProposalCopy = {
  propose: {
    title: 'Propose a task',
    titleLabel: 'Title',
    titleHint: 'One sentence naming the outcome.',
    typeLabel: 'Type',
    typeDefinitions: {
      DEVELOPMENT: 'Code, infrastructure, or tooling for the product.',
      RESEARCH_AND_DEVELOPMENT:
        'Research, prototypes, and experiments — work whose output is a finding as often as a feature.',
      ANNOTATION: 'Labeling, review, or correction of data.',
    },
    difficultyLabel: 'Size',
    difficultyDefinitions: {
      EXTRA_SMALL: 'Under an hour.',
      SMALL: 'A few hours.',
      MEDIUM: 'A day or two.',
      LARGE: 'Several days.',
      EXTRA_LARGE: 'A week or more; consider splitting.',
    },
    suggestedPriorityLabel: 'Suggested priority',
    suggestedPriorityHint:
      'A suggestion only. An owner sets the authoritative priority at approval.',
    descriptionLabel: 'Description',
    descriptionHint:
      'Markdown. Say what done looks like — include acceptance criteria as a list.',
    prerequisitesLabel: 'Prerequisites (optional)',
    prerequisitesHint:
      'Tasks this one must wait for. It cannot be approved until they are Done.',
    prerequisitesSearchLabel: 'Search tasks',
    prerequisitesSearchPlaceholder: 'Filter by reference or title…',
    prerequisitesLoading: 'Loading the task list…',
    prerequisitesFailed: 'The task list could not be loaded.',
    prerequisitesEmpty: 'There are no other tasks to depend on yet.',
    prerequisitesNoMatches: 'No task matches this search.',
    selectedPrerequisites: (count) =>
      count === 1
        ? '1 prerequisite selected'
        : `${String(count)} prerequisites selected`,
    removePrerequisite: (reference) => `Remove ${reference}`,
    submit: 'Propose into Backlog',
    submitting: 'Proposing…',
    successTitle: 'Proposed into Backlog',
    successBody: 'An owner will review it. You can follow it from the board.',
    viewTask: 'View the task',
    errorSummary: 'The proposal was not sent. Fix the following and try again.',
    titleMissing: 'A title is required.',
    descriptionMissing: 'A description is required.',
  },

  markdown: {
    edit: 'Edit',
    preview: 'Preview',
    previewEmpty: 'Nothing to preview yet.',
    previewLabel: 'Preview of your description',
  },
};

export const TASK_PROPOSAL_COPY_AR: TaskProposalCopy = {
  propose: {
    title: 'اقتراح مهمة',
    titleLabel: 'العنوان',
    titleHint: 'جملة واحدة تسمّي النتيجة.',
    typeLabel: 'النوع',
    typeDefinitions: {
      DEVELOPMENT: 'برمجة أو بنية تحتية أو أدوات للمنتج.',
      RESEARCH_AND_DEVELOPMENT:
        'بحث ونماذج أولية وتجارب — عمل نتيجته اكتشاف بقدر ما هي ميزة.',
      ANNOTATION: 'تصنيف البيانات أو مراجعتها أو تصحيحها.',
    },
    difficultyLabel: 'الحجم',
    difficultyDefinitions: {
      EXTRA_SMALL: 'أقل من ساعة.',
      SMALL: 'بضع ساعات.',
      MEDIUM: 'يوم أو يومان.',
      LARGE: 'عدة أيام.',
      EXTRA_LARGE: 'أسبوع أو أكثر؛ فكّر في تقسيمها.',
    },
    suggestedPriorityLabel: 'الأولوية المقترحة',
    suggestedPriorityHint: 'اقتراح فقط. يحدد المالك الأولوية المعتمدة عند الاعتماد.',
    descriptionLabel: 'الوصف',
    descriptionHint: 'بصيغة Markdown. صف شكل الإنجاز — وأدرج معايير القبول كقائمة.',
    prerequisitesLabel: 'متطلبات سابقة (اختياري)',
    prerequisitesHint: 'مهام يجب أن تنتظرها هذه المهمة. لا تُعتمد حتى تُنجز جميعها.',
    prerequisitesSearchLabel: 'ابحث في المهام',
    prerequisitesSearchPlaceholder: 'رشِّح بالمرجع أو العنوان…',
    prerequisitesLoading: 'يجري تحميل قائمة المهام…',
    prerequisitesFailed: 'تعذّر تحميل قائمة المهام.',
    prerequisitesEmpty: 'لا توجد مهام أخرى للاعتماد عليها بعد.',
    prerequisitesNoMatches: 'لا توجد مهمة تطابق هذا البحث.',
    selectedPrerequisites: (count) =>
      count === 1 ? 'متطلب سابق واحد محدد' : `${String(count)} متطلبات سابقة محددة`,
    removePrerequisite: (reference) => `إزالة ${reference}`,
    submit: 'اقتراح إلى المقترحات',
    submitting: 'جارٍ الاقتراح…',
    successTitle: 'أُضيف إلى المقترحات',
    successBody: 'سيراجعه أحد المالكين. يمكنك متابعته من اللوحة.',
    viewTask: 'عرض المهمة',
    errorSummary: 'لم يُرسل الاقتراح. صحّح ما يلي وحاول مجددًا.',
    titleMissing: 'العنوان مطلوب.',
    descriptionMissing: 'الوصف مطلوب.',
  },

  markdown: {
    edit: 'تحرير',
    preview: 'معاينة',
    previewEmpty: 'لا شيء للمعاينة بعد.',
    previewLabel: 'معاينة وصفك',
  },
};
